import { servicesUrlConfig } from "shared/config/setting";

/**
 * Organization Storages API Service (multi-storage registry).
 * Centralized API calls for an organization's storages, replication rules,
 * and replication-agent keys.
 *
 * NOTE: unlike the other org endpoints (auth service), these endpoints live
 * on the RAASDATA service: /raasdata/api/v1/orgs/{orgId}/storages
 */
export const orgStoragesService = {
  /**
   * List the organization's storages. The platform's central (system)
   * storage row is included read-only; robot-local storages are excluded.
   * @param {string} orgId - Organization ID
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response with an array of storages
   */
  async list(orgId, authFetch) {
    return await authFetch(servicesUrlConfig.RAASDATA_API.org_storages(orgId));
  },

  /**
   * Create a new storage.
   * `config.secret_key` is WRITE-ONLY: it is never returned by the API
   * (`config.has_secret` tells whether one is stored).
   * @param {string} orgId - Organization ID
   * @param {Object} data - {name, storage_type, managed, config, enabled}
   * @param {function} authFetch - Authenticated fetch function
   */
  async create(orgId, data, authFetch) {
    return await authFetch(servicesUrlConfig.RAASDATA_API.org_storages(orgId), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  },

  /**
   * Partially update a storage. Omit `config.secret_key` to keep the stored
   * secret. Disabling/unmanaging a storage with live replicas returns 409
   * STORAGE_IN_USE unless `force` is set.
   * @param {string} orgId - Organization ID
   * @param {string} storageId - Storage ID
   * @param {Object} data - Partial update payload
   * @param {function} authFetch - Authenticated fetch function
   * @param {boolean} [force] - Override the in-use guard
   */
  async update(orgId, storageId, data, authFetch, force = false) {
    const base = servicesUrlConfig.RAASDATA_API.org_storage(orgId, storageId);
    return await authFetch(force ? `${base}?force=true` : base, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(data),
    });
  },

  /**
   * Delete a storage. Returns 409 STORAGE_IN_USE (with replica/rule counts)
   * unless `force` is set — forcing may orphan bytes on the org's storage.
   * @param {string} orgId - Organization ID
   * @param {string} storageId - Storage ID
   * @param {function} authFetch - Authenticated fetch function
   * @param {boolean} [force] - Override the in-use guard
   */
  async remove(orgId, storageId, authFetch, force = false) {
    const base = servicesUrlConfig.RAASDATA_API.org_storage(orgId, storageId);
    return await authFetch(force ? `${base}?force=true` : base, {
      method: "DELETE",
    });
  },

  /**
   * Test server-side connectivity to a storage (managed storages only —
   * unmanaged ones respond with testable=false).
   * @param {string} orgId - Organization ID
   * @param {string} storageId - Storage ID
   * @param {function} authFetch - Authenticated fetch function
   * @returns {Promise} API response {storage_id, testable, ok, detail}
   */
  async testConnection(orgId, storageId, authFetch) {
    return await authFetch(
      servicesUrlConfig.RAASDATA_API.org_storage_test_connection(
        orgId,
        storageId
      ),
      { method: "POST" }
    );
  },

  /**
   * Make this storage the organization's upload target (new robot uploads
   * land here). Atomically clears the flag on sibling storages.
   * @param {string} orgId - Organization ID
   * @param {string} storageId - Storage ID
   * @param {function} authFetch - Authenticated fetch function
   */
  async setUploadTarget(orgId, storageId, authFetch) {
    return await authFetch(
      servicesUrlConfig.RAASDATA_API.org_storage_set_upload_target(
        orgId,
        storageId
      ),
      { method: "POST" }
    );
  },

  /**
   * Clear the org's upload target — new uploads fall back to the central
   * (system) storage.
   * @param {string} orgId - Organization ID
   * @param {function} authFetch - Authenticated fetch function
   */
  async clearUploadTarget(orgId, authFetch) {
    return await authFetch(
      servicesUrlConfig.RAASDATA_API.org_clear_upload_target(orgId),
      { method: "DELETE" }
    );
  },
};

/**
 * Replication rules of a (source) storage: files that land on the source are
 * copied or moved to the target storage by the replication worker/agents.
 */
export const replicationRulesService = {
  /**
   * Add a rule on a source storage.
   * @param {string} orgId - Organization ID
   * @param {string} storageId - SOURCE storage ID
   * @param {Object} data - {target_storage_id, mode: "copy"|"move", enabled}
   * @param {function} authFetch - Authenticated fetch function
   */
  async create(orgId, storageId, data, authFetch) {
    return await authFetch(
      servicesUrlConfig.RAASDATA_API.replication_rules(orgId, storageId),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
  },

  /**
   * Update a rule (mode/enabled).
   * @param {string} orgId - Organization ID
   * @param {string} storageId - SOURCE storage ID
   * @param {string} ruleId - Rule ID
   * @param {Object} data - {mode?, enabled?}
   * @param {function} authFetch - Authenticated fetch function
   */
  async update(orgId, storageId, ruleId, data, authFetch) {
    return await authFetch(
      servicesUrlConfig.RAASDATA_API.replication_rule(orgId, storageId, ruleId),
      {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
  },

  /**
   * Delete a rule (in-flight replicas it created are untouched).
   * @param {string} orgId - Organization ID
   * @param {string} storageId - SOURCE storage ID
   * @param {string} ruleId - Rule ID
   * @param {function} authFetch - Authenticated fetch function
   */
  async remove(orgId, storageId, ruleId, authFetch) {
    return await authFetch(
      servicesUrlConfig.RAASDATA_API.replication_rule(orgId, storageId, ruleId),
      { method: "DELETE" }
    );
  },
};

/**
 * Replication-agent keys: credentials for org-side workers that copy/delete
 * replicas on storages the platform cannot reach.
 */
export const replicationAgentsService = {
  /**
   * List the org's agent keys (tokens are never returned — only prefixes).
   * @param {string} orgId - Organization ID
   * @param {function} authFetch - Authenticated fetch function
   */
  async list(orgId, authFetch) {
    return await authFetch(
      servicesUrlConfig.RAASDATA_API.replication_agents(orgId)
    );
  },

  /**
   * Issue a new agent key. The response contains the plaintext `token`
   * EXACTLY ONCE — show it to the admin and never store it client-side.
   * @param {string} orgId - Organization ID
   * @param {Object} data - {name, storage_ids?}
   * @param {function} authFetch - Authenticated fetch function
   */
  async create(orgId, data, authFetch) {
    return await authFetch(
      servicesUrlConfig.RAASDATA_API.replication_agents(orgId),
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }
    );
  },

  /**
   * Revoke an agent key (rotation = revoke + create).
   * @param {string} orgId - Organization ID
   * @param {string} agentId - Agent ID
   * @param {function} authFetch - Authenticated fetch function
   */
  async remove(orgId, agentId, authFetch) {
    return await authFetch(
      servicesUrlConfig.RAASDATA_API.replication_agent(orgId, agentId),
      { method: "DELETE" }
    );
  },
};

// Backward-compatible alias removed with the multi-storage clean break:
// the old singleton `orgStorageService` (PUT /storage-config) no longer
// exists on the server.
